import { useMutation } from "@apollo/client/react";
import { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import { useFormFunnelTracking } from "../hooks/useFormFunnelTracking";
import { SubmitFormFunnelLeadDocument } from "../hooks/generated";
import { getUrls } from "@portal/app/_common/lib/urls";
import { logger } from "@portal/app/_common/lib/logger";
import type { ProgramEnvironment, ProgramMetadata } from "@portal/app/_common/types/program";
import type {
  PayloadProgramHighlight,
  PayloadResultScenarios,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { ProgramResultCard } from "./ProgramResultCard";
import { prepareDreamSearchClickFallback } from "@portal/app/dream-search-click/actions";
import { parseValidHttpUrl } from "@portal/app/_common/lib/parseValidHttpUrl";
import { useHostedStepAction } from "./hostedSteps/useHostedStepAction";
import { useFormFunnelHeadingLevel } from "../FormFunnelHeadingContext";

export interface ProgramClickoutData {
  id: string;
  name: string;
  slug?: string;
  environment?: ProgramEnvironment;
  leadRedirect: string | null;
  preLeadRedirectParams?: ProgramMetadata["preLeadRedirectParams"] | null;
  preLeadRedirectCompositeParams?: ProgramMetadata["preLeadRedirectCompositeParams"] | null;
  preLeadRedirectSuppressDefaultParams?: boolean | null;
  customCtaCopy?: string | null;
  customDisclaimer?: string | null;
  programHighlights?: string[];
  hasFinancialAidOption?: boolean;
  provider: {
    id: string;
    slug?: string;
    name: string;
    shortName?: string;
    advertiserName?: string;
    isTrustedFormEnabled?: boolean;
    logoImageFile?: { url: string | null } | null;
  };
}

export interface ClickoutRedirectArgs {
  program: ProgramClickoutData;
  sessionId?: string;
  /** When true, prevents actual redirect and shows preview indicator */
  isPreview?: boolean;
  /** Form funnel ID for lead submission */
  funnelId?: string;
  /** Per-tab form funnel attempt ID for linking the session row to an enrollment */
  formFunnelSessionId?: string;
  /** Collected form answers for lead submission */
  answers?: Record<string, unknown>;
  /**
   * Pre-ping result payload from getFormFunnelMatches, threaded through to the
   * submit mutation so the server can persist persistedFields (e.g. PEC's
   * CecUniqueId) onto enrollment_item.metadata.prePingResult.
   */
  prePingResult?: unknown;
  /** Displayed result-card rank selected by the student. */
  selectedResultRank?: number;
}

interface ClickoutResultProps extends ClickoutRedirectArgs {
  clickoutConfig?: PayloadResultScenarios["clickoutConfig"];
  /** Static program highlights from CMS config. Overrides program.programHighlights when provided. */
  staticProgramHighlights?: PayloadProgramHighlight[];
}

/**
 * Replaces {{token}} placeholders in a URL with provided values.
 * Tokens are case-insensitive. Unmatched tokens are removed to avoid
 * sending literal "{{token}}" strings to external redirect targets.
 */
const resolveRedirectTokens = (
  url: string,
  tokens: Record<string, string | undefined | null>
): string => {
  const lowerMap = new Map<string, string>();
  for (const [k, v] of Object.entries(tokens)) {
    if (v) lowerMap.set(k.toLowerCase(), v);
  }
  return url.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key: string) => {
    return lowerMap.get(key.toLowerCase()) ?? "";
  });
};

/**
 * Constructs the final redirect URL with tracking params appended.
 * Resolves {{token}} placeholders (e.g., {{marketplaceEventId}}, {{customerId}})
 * in the redirect URL before appending query parameters.
 */
const buildRedirectUrl = (
  program: ProgramClickoutData,
  params: {
    marketplaceEventId?: string | null;
    customerId?: string | null;
    everflowTransactionId?: string | null;
    enrollmentItemId?: string | null;
  }
): string | null => {
  if (!program.leadRedirect) return null;

  const resolvedUrl = parseValidHttpUrl(
    resolveRedirectTokens(program.leadRedirect, {
      marketplaceEventId: params.marketplaceEventId,
      customerId: params.customerId,
      everflowTransactionId: params.everflowTransactionId,
      enrollmentItemId: params.enrollmentItemId,
      programSlug: program.slug,
      programId: program.id,
    })
  );
  if (!resolvedUrl) return null;

  return getUrls().PreLeadRedirect(
    {
      preLeadRedirect: resolvedUrl,
      preLeadRedirectParams: program.preLeadRedirectParams,
      preLeadRedirectCompositeParams: program.preLeadRedirectCompositeParams,
      preLeadRedirectSuppressDefaultParams: program.preLeadRedirectSuppressDefaultParams,
    },
    {
      marketplaceEventId: params.marketplaceEventId ?? undefined,
      customerId: params.customerId ?? undefined,
      everflowTransactionId: params.everflowTransactionId ?? undefined,
      programSlug: program.slug,
      // NOTE: userZip is intentionally not available on the form-funnel
      // clickout surface (only on dream-search-click). A composite component
      // referencing `userZip` will therefore render blank here; the assembly
      // guards by emitting an empty value rather than failing. UMA does not
      // use userZip, so this asymmetry is documented rather than addressed.
    }
  );
};

export const useClickoutRedirect = ({
  program,
  sessionId,
  isPreview = false,
  funnelId,
  formFunnelSessionId,
  answers,
  prePingResult,
  selectedResultRank,
}: ClickoutRedirectArgs) => {
  const { getTrackingIds, trackEvent, trackHostedFlowStep } = useFormFunnelTracking();
  const [submitFormFunnelLead] = useMutation(SubmitFormFunnelLeadDocument);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const action = useHostedStepAction(program.id);

  const handleDirectRedirect = useCallback(
    async (generation: number) => {
      const destination = parseValidHttpUrl(program.leadRedirect);
      if (!destination) {
        window.location.href = getUrls().Explore();
        return;
      }

      let trackingIds: Awaited<ReturnType<typeof getTrackingIds>> | undefined;
      try {
        trackingIds = await getTrackingIds();
      } catch {
        // Compliance tracking is best-effort; the server action still enforces suppression.
      }

      try {
        const prepared = await prepareDreamSearchClickFallback({
          programId: program.id,
          jornayaLeadId: trackingIds?.jornayaLeadId ?? undefined,
          formFunnelId: funnelId,
          existingMarketplaceEventId: trackingIds?.marketplaceEventId ?? undefined,
        });
        if (prepared.kind === "redirect") {
          window.location.href = prepared.url;
          return;
        }

        const finalUrl = buildRedirectUrl(
          { ...program, leadRedirect: destination },
          {
            marketplaceEventId:
              prepared.marketplaceEventId ?? trackingIds?.marketplaceEventId ?? undefined,
            customerId: prepared.customerId ?? trackingIds?.customerId ?? undefined,
            everflowTransactionId:
              prepared.everflowTransactionId ?? trackingIds?.everflowTransactionId ?? undefined,
          }
        );
        if (!action.complete(generation)) return;
        if (finalUrl) trackHostedFlowStep("school_match");
        window.location.href = finalUrl ?? getUrls().Explore();
      } catch (error) {
        logger.error(error, "ClickoutResult: guarded clickout preparation failed");
        window.location.href = getUrls().Explore();
      }
    },
    [funnelId, getTrackingIds, program, action, trackHostedFlowStep]
  );

  const handleRedirect = useCallback(async () => {
    if (isPreview) return;
    const generation = action.begin();
    if (generation === undefined) return;

    setIsRedirecting(true);

    trackEvent("Form Funnel - Clickout Clicked", {
      programId: program.id,
      providerId: program.provider.id,
      destinationUrl: program.leadRedirect,
      sessionId,
      ...(selectedResultRank ? { selectedResultRank } : {}),
    });

    if (funnelId) {
      try {
        const trackingIds = await getTrackingIds();

        const { data } = await submitFormFunnelLead({
          variables: {
            funnelId,
            programId: program.id,
            answers: answers || {},
            formFunnelSessionId,
            ...(selectedResultRank ? { selectedResultRank } : {}),
            complianceTracking: {
              jornayaLeadId: trackingIds.jornayaLeadId || "",
              trustedFormCertId: trackingIds.trustedFormCertId,
              // See ConfirmStep: forwarded because in-app browsers drop the
              // ef_* cookies from requests mid-funnel.
              everflowTransactionId: trackingIds.everflowTransactionId,
              fbPixelBrowserId: trackingIds.facebookBrowserId,
              fbPixelClickId: trackingIds.facebookClickId,
              marketplaceEventId: trackingIds.marketplaceEventId,
              // Forward so the server-created DREAMSEARCH_CLICK event carries
              // gaClientId in its metadata;
              // createClickoutEnrollment needs it to fire the GA purchase
              // event.
              gaClientId: trackingIds.gaClientId,
              googleClickId: trackingIds.googleClickId,
              googleWbraid: trackingIds.googleWbraid,
              googleGbraid: trackingIds.googleGbraid,
              googleAdsCapturedAt: trackingIds.googleAdsCapturedAt,
              everflowOfferId: trackingIds.everflowOfferId,
              everflowAffiliateId: trackingIds.everflowAffiliateId,
              everflowSub1: trackingIds.everflowSub1,
              everflowSub2: trackingIds.everflowSub2,
              everflowSub3: trackingIds.everflowSub3,
              everflowSub4: trackingIds.everflowSub4,
              everflowSub5: trackingIds.everflowSub5,
              everflowSub6: trackingIds.everflowSub6,
              everflowSub7: trackingIds.everflowSub7,
              everflowSub8: trackingIds.everflowSub8,
            },
            ...(program.customDisclaimer ? { disclaimer: program.customDisclaimer } : {}),
            ...(prePingResult != null ? { prePingResult } : {}),
          },
        });

        const result = data?.submitFormFunnelLead;

        if (result?.scenario === "PROGRAM_UNAVAILABLE") {
          window.location.href =
            program.provider.slug && program.slug
              ? getUrls().ProviderProgramStorefront(program.provider.slug, program.slug)
              : getUrls().Explore();
          return;
        }

        if (result?.eligibilityFailure) {
          logger.warn(
            { eligibilityFailure: result.eligibilityFailure },
            "ClickoutResult: unexpected eligibility failure"
          );
        }

        if (result?.success && result.enrollmentItemId && !result.eligibilityFailure) {
          trackEvent(
            "Form Funnel - Enrollment Linked",
            {
              enrollmentItemId: result.enrollmentItemId,
              formFunnelId: funnelId,
              formFunnelSessionId,
              programId: program.id,
              providerId: program.provider.id,
              scenario: result.scenario,
            },
            { transport: "sendBeacon", send_immediately: true }
          );
        }
        const redirectUrl = result?.redirectUrl;
        if (redirectUrl) {
          const finalUrl = buildRedirectUrl(program, {
            // Hybrid program-specific funnels retain their mount-time event;
            // pure clickouts use the event returned after the suppression gate.
            marketplaceEventId: result?.marketplaceEventId ?? trackingIds.marketplaceEventId,
            customerId: result?.customerId,
            everflowTransactionId:
              result?.everflowTransactionId ?? trackingIds.everflowTransactionId,
            enrollmentItemId: result?.enrollmentItemId,
          });
          if (!action.complete(generation)) return;
          if (finalUrl && result.success && !result.eligibilityFailure) {
            trackHostedFlowStep("school_match");
          }
          window.location.href = finalUrl ?? getUrls().Explore();
        } else {
          window.location.href = getUrls().Explore();
        }
        return;
      } catch (e) {
        logger.error(
          e,
          "ClickoutResult: submitFormFunnelLead failed, using guarded clickout redirect"
        );
        await handleDirectRedirect(generation);
        return;
      }
    }

    await handleDirectRedirect(generation);
  }, [
    program,
    sessionId,
    isPreview,
    getTrackingIds,
    trackEvent,
    funnelId,
    formFunnelSessionId,
    answers,
    submitFormFunnelLead,
    handleDirectRedirect,
    prePingResult,
    selectedResultRank,
    action,
    trackHostedFlowStep,
  ]);

  return { handleRedirect, isRedirecting };
};

export const ClickoutResult = ({
  program,
  clickoutConfig,
  sessionId,
  isPreview = false,
  staticProgramHighlights,
  funnelId,
  formFunnelSessionId,
  answers,
  prePingResult,
  selectedResultRank,
}: ClickoutResultProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const { handleRedirect, isRedirecting } = useClickoutRedirect({
    program,
    sessionId,
    isPreview,
    funnelId,
    formFunnelSessionId,
    answers,
    prePingResult,
    selectedResultRank,
  });

  return (
    <Box data-testid="clickout-result" sx={{ width: "100%", mx: "auto" }}>
      <ProgramResultCard
        option={{
          type: "CLICKOUT",
          rank: 1,
          program,
          staticProgramHighlights,
          prePingResult,
        }}
        isSubmitting={isRedirecting}
        isPreview={isPreview}
        clickoutCtaText={clickoutConfig?.ctaText}
        programHeadingComponent={headingLevel}
        onSelect={() => {
          if (isRedirecting || isPreview) return;
          void handleRedirect();
        }}
        ctaTestId="clickout-cta-button"
      />
    </Box>
  );
};
